import React from "react";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	If,
	Image,
	makeClassName,
	Modal,
	Row,
	StyleProps,
	toGrammaticalNumber,
} from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import {
	permissions as allPermissions,
	ContractComponents,
	StaticURLs,
	useElementSize,
	useParentPortalReEnrollmentContractsQuery,
} from "@hex-insights/verita-romania.shared";
import styles from "./styles.module.css";

const requiredPermissions: string[] = [allPermissions.Contract.View.Admin, allPermissions.Contract.View.Basic];

export type ReEnrollmentContractsNoticePermissionRequiredProps = Partial<ChildrenProps>;

export function ReEnrollmentContractsNoticePermissionRequired({
	children,
}: ReEnrollmentContractsNoticePermissionRequiredProps) {
	return (
		<PermissionRequired requiredPermissions={requiredPermissions} checkMethod="or">
			{children}
		</PermissionRequired>
	);
}

export type ReEnrollmentContractsNoticeProps = Partial<ClassNameProps & StyleProps>;

export function ReEnrollmentContractsNotice({ className, style }: ReEnrollmentContractsNoticeProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { loading, data } = useParentPortalReEnrollmentContractsQuery({
		variables: {
			filters: {
				isApprovedEQ: true,
				fileKeyNEQ: "",
				academicTerms: [
					{
						academicYear: [
							{
								nameEQ: "2024-2025", // TODO dynamic year
							},
						],
					},
				],
				student: [
					{
						relationships: [
							{
								guardian: [
									{
										person: [
											{
												user: [
													{
														isCurrentEQ: true,
													},
												],
											},
										],
									},
								],
							},
						],
					},
				],
			},
		},
	});

	const shouldNotRender = loading || !data || data.contractConnection.edges.length === 0;

	const containerRef = React.useRef<HTMLDivElement | null>(null);
	const { width } = useElementSize(containerRef, shouldNotRender);
	const isNarrow = width < 600;

	if (shouldNotRender) {
		return null;
	}

	const numContracts = data.contractConnection.edges.length;

	return (
		<React.Fragment>
			<div ref={containerRef} onClick={openModal}>
				<Row
					justify="space-between"
					align="center"
					className={makeClassName(
						styles["report-cards-notice"],
						isNarrow ? styles["report-cards-notice--narrow"] : "",
						className,
					)}
					style={{ rowGap: "1rem", ...style }}
				>
					<Column justify="spaced-start" verticalSpacing="1rem" style={{ flexGrow: 1 }}>
						<Column justify="spaced-start" verticalSpacing="0.5rem">
							<span className={styles["report-cards-notice__heading"]}>Re-Enrollment Contracts Now Available</span>

							<span>
								Please click the button below to download your{" "}
								{numContracts === 1 ? "student's contract" : "students' contracts"}.
							</span>
						</Column>

						<Button variant="primary" onClick={openModal}>
							Download {toGrammaticalNumber("Contract", numContracts)}
						</Button>
					</Column>

					<If condition={!isNarrow}>
						<Image src={StaticURLs.Images.reportCardGraphic} sizing="height-scaled" style={{ height: "100%" }} />
					</If>
				</Row>
			</div>

			<Modal.If condition={isModalOpen}>
				<ContractComponents.ContractsGuardianDownloadModal data={data} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}
